import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api.js";
import type { ReviewItem } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";

interface Props {
  projectId: string;
  onChanged?: () => void;
}

const KIND_LABEL: Record<string, string> = {
  "duplicate-locations": "Duplicate locations",
  "duplicate-threads": "Overlapping plot threads",
  "duplicate-timeline": "Duplicate timeline events",
  "object-location": "Object, not a location",
  "generic-location": "Unresolved location",
  "sublocation-parent": "Sublocation",
  "relationship-consolidation": "Relationship to consolidate",
  "canon-conflict": "Canon conflict",
  "low-confidence": "Low-confidence suggestion",
  "ambiguous-reference": "Ambiguous reference",
  "chronology-conflict": "Chronology conflict",
  other: "Review"
};

/**
 * The central Story Intelligence "Needs Review" queue. Uncertain extractions and
 * detected duplicates land here instead of polluting canon; the writer applies
 * or dismisses each. Nothing is changed automatically.
 */
export function NeedsReviewPanel({ projectId, onChanged }: Props) {
  const toast = useToast();
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [keepChoice, setKeepChoice] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { items } = await api.reviewQueue(projectId, "open");
      setItems(items);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't load the review queue");
    } finally {
      setLoading(false);
    }
  }, [projectId, toast]);

  useEffect(() => { void load(); }, [load]);

  const scan = async () => {
    setScanning(true);
    try {
      const s = await api.runCleanup(projectId, true);
      toast.success(`Scan complete — ${s.openReview} item${s.openReview === 1 ? "" : "s"} to review (${s.autoApplied} safe fix${s.autoApplied === 1 ? "" : "es"} applied).`);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const act = async (item: ReviewItem, action: string) => {
    setBusyId(item.id);
    try {
      if (action === "dismiss") {
        await api.dismissReview(projectId, item.id);
      } else {
        const keepId = keepChoice[item.id] ?? (item.payload.keepId as string | undefined);
        await api.resolveReview(projectId, item.id, { action, keepId });
      }
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      onChanged?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <div className="insights-loading"><Icon name="sparkles" size={22} className="spin" style={{ color: "var(--accent)" }} /></div>;
  }

  return (
    <div className="review">
      <div className="review-head">
        <p className="faint" style={{ fontSize: 13, margin: 0 }}>
          {items.length === 0
            ? "Nothing to review — your Story Intelligence is clean."
            : `${items.length} item${items.length === 1 ? "" : "s"} need your decision. Nothing is changed until you act.`}
        </p>
        <button className="btn btn-sm" onClick={scan} disabled={scanning}>
          <Icon name={scanning ? "clock" : "search"} size={14} className={scanning ? "spin" : ""} /> {scanning ? "Scanning…" : "Scan for issues"}
        </button>
      </div>

      {items.length === 0 ? (
        <div className="review-empty card">
          <Icon name="check" size={20} style={{ color: "var(--success)" }} />
          <span>All clear. Run a scan after extracting to catch duplicates and object-locations.</span>
        </div>
      ) : (
        <ul className="review-list">
          {items.map((item) => (
            <li key={item.id} className="review-item card">
              <div className="review-item-head">
                <span className="chip review-kind">{KIND_LABEL[item.kind] ?? item.kind}</span>
                <span className="strong truncate">{item.title}</span>
              </div>
              {item.detail && <p className="review-detail">{item.detail}</p>}
              {item.evidence && <p className="review-evidence faint">“{item.evidence}”</p>}

              {renderCandidates(item, keepChoice, setKeepChoice)}

              <div className="review-actions">
                {renderActions(item, busyId === item.id, act)}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function renderCandidates(
  item: ReviewItem,
  keepChoice: Record<string, string>,
  setKeepChoice: (f: (p: Record<string, string>) => Record<string, string>) => void
) {
  const names = (item.payload.names ?? item.payload.titles) as { id: string; name?: string; title?: string }[] | undefined;
  if (!names || names.length === 0) return null;
  const selected = keepChoice[item.id] ?? (item.payload.keepId as string | undefined) ?? names[0]!.id;
  return (
    <div className="review-candidates">
      <span className="faint" style={{ fontSize: 11 }}>Keep which one?</span>
      {names.map((n) => (
        <label key={n.id} className={`review-candidate ${selected === n.id ? "is-keep" : ""}`}>
          <input
            type="radio"
            name={`keep-${item.id}`}
            checked={selected === n.id}
            onChange={() => setKeepChoice((p) => ({ ...p, [item.id]: n.id }))}
          />
          {n.name ?? n.title}
        </label>
      ))}
    </div>
  );
}

function renderActions(item: ReviewItem, busy: boolean, act: (item: ReviewItem, action: string) => void) {
  const Btn = ({ action, label, primary, danger }: { action: string; label: string; primary?: boolean; danger?: boolean }) => (
    <button
      className={`btn btn-sm ${primary ? "btn-primary" : danger ? "btn-danger" : "btn-ghost"}`}
      disabled={busy}
      onClick={() => act(item, action)}
    >
      {label}
    </button>
  );
  switch (item.kind) {
    case "duplicate-locations":
    case "duplicate-threads":
    case "duplicate-timeline":
      return <><Btn action="merge" label="Merge" primary /><Btn action="dismiss" label="Keep separate" /></>;
    case "object-location":
      return <><Btn action="archive" label="Archive (not a location)" primary /><Btn action="dismiss" label="Keep as location" /></>;
    case "generic-location":
      return <><Btn action="archive" label="Archive" primary /><Btn action="dismiss" label="Keep" /></>;
    case "sublocation-parent":
      return <><Btn action="set-parent" label="Make it a sublocation" primary /><Btn action="dismiss" label="Keep separate" /></>;
    case "relationship-consolidation":
      return <><Btn action="consolidate" label="Consolidate" primary /><Btn action="dismiss" label="Keep all" /></>;
    case "low-confidence":
      return <><Btn action="accept" label="Add to canon" primary /><Btn action="dismiss" label="Discard" danger /></>;
    default:
      return <Btn action="dismiss" label="Dismiss" />;
  }
}
